import React, { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import styled from 'styled-components'
import { ZoomIn } from 'lucide-react'

const MIN_SCALE = 1
const MAX_SCALE = 5
export const DEFAULT_COVER_SCALE = 1
export const DEFAULT_COVER_POS = 50

/** Mesma proporção do card de capa no quadro (BoardCoverArea: largura base 320px, altura 140px). */
const PREVIEW_WIDTH = 480
const PREVIEW_HEIGHT = 210

const clampPercent = (value: number) => Math.min(100, Math.max(0, value))

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 4000;
`

const Panel = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  width: min(90vw, 560px);
  box-shadow: 0 20px 48px rgba(0, 0, 0, 0.25);
`

const Title = styled.h3`
  margin: 0 0 4px;
  font-size: 18px;
  font-weight: 700;
  color: #101010;
`

const Subtitle = styled.p`
  margin: 0 0 16px;
  font-size: 13px;
  color: #767676;
`

const PreviewFrame = styled.div<{ $dragging: boolean }>`
  position: relative;
  width: ${PREVIEW_WIDTH}px;
  max-width: 100%;
  height: ${PREVIEW_HEIGHT}px;
  margin: 0 auto 20px;
  border-radius: 12px;
  overflow: hidden;
  background: #f1f1f1;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${(props) => (props.$dragging ? 'grabbing' : 'grab')};
  touch-action: none;
`

const PreviewImage = styled.img<{ $scale: number; $posX: number; $posY: number }>`
  width: 100%;
  height: 100%;
  object-fit: contain;
  transform-origin: ${(props) => props.$posX}% ${(props) => props.$posY}%;
  transform: scale(${(props) => props.$scale});
  transition: transform 0.05s linear;
  pointer-events: none;
  user-select: none;
`

const FocusMarker = styled.div<{ $posX: number; $posY: number }>`
  position: absolute;
  left: ${(props) => props.$posX}%;
  top: ${(props) => props.$posY}%;
  width: 14px;
  height: 14px;
  margin: -7px 0 0 -7px;
  border-radius: 50%;
  border: 2px solid #fff;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.4);
  background: #0090d3;
  pointer-events: none;
`

const SliderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 24px;
`

const SliderInput = styled.input.attrs<{ min?: number; max?: number }>((props) => ({
  type: 'range',
  min: props.min ?? MIN_SCALE,
  max: props.max ?? MAX_SCALE,
  step: 0.01,
}))<{ min?: number; max?: number }>`
  flex: 1;
  height: 4px;
  appearance: none;
  background: #e0e0e0;
  border-radius: 2px;
  outline: none;
  cursor: pointer;

  &::-webkit-slider-thumb {
    appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #0090d3;
    cursor: pointer;
  }

  &::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border: none;
    border-radius: 50%;
    background: #0090d3;
    cursor: pointer;
  }
`

const ZoomValue = styled.span`
  min-width: 44px;
  text-align: right;
  font-size: 13px;
  font-weight: 600;
  color: #444;
`

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`

const CancelButton = styled.button`
  padding: 10px 18px;
  border-radius: 8px;
  border: 1px solid #d5d5d5;
  background: #fff;
  color: #444;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #f5f5f5;
  }
`

const SaveButton = styled.button`
  padding: 10px 18px;
  border-radius: 8px;
  border: none;
  background: #0090d3;
  color: #fff;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #0077b3;
  }
`

interface CoverScaleModalProps {
  isOpen: boolean
  imageUrl: string
  imageAlt: string
  title?: string
  subtitle?: string
  minScale?: number
  maxScale?: number
  initialScale?: number | null
  initialPosX?: number | null
  initialPosY?: number | null
  onCancel: () => void
  onConfirm: (scale: number, posX: number, posY: number) => void
}

const CoverScaleModal: React.FC<CoverScaleModalProps> = ({
  isOpen,
  imageUrl,
  imageAlt,
  title = 'Definir capa do produto',
  subtitle = 'Ajuste o zoom e arraste a imagem para escolher a área que vai aparecer no quadro.',
  minScale = MIN_SCALE,
  maxScale = MAX_SCALE,
  initialScale,
  initialPosX,
  initialPosY,
  onCancel,
  onConfirm,
}) => {
  const [scale, setScale] = useState<number>(initialScale ?? DEFAULT_COVER_SCALE)
  const [posX, setPosX] = useState<number>(initialPosX ?? DEFAULT_COVER_POS)
  const [posY, setPosY] = useState<number>(initialPosY ?? DEFAULT_COVER_POS)
  const [isDragging, setIsDragging] = useState(false)
  const frameRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isOpen) {
      setScale(initialScale ?? DEFAULT_COVER_SCALE)
      setPosX(initialPosX ?? DEFAULT_COVER_POS)
      setPosY(initialPosY ?? DEFAULT_COVER_POS)
    }
  }, [isOpen, initialScale, initialPosX, initialPosY])

  if (!isOpen) return null

  const updatePositionFromPointer = (clientX: number, clientY: number) => {
    const rect = frameRef.current?.getBoundingClientRect()
    if (!rect) return
    setPosX(clampPercent(((clientX - rect.left) / rect.width) * 100))
    setPosY(clampPercent(((clientY - rect.top) / rect.height) * 100))
  }

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.currentTarget.setPointerCapture(e.pointerId)
    setIsDragging(true)
    updatePositionFromPointer(e.clientX, e.clientY)
  }

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!isDragging) return
    updatePositionFromPointer(e.clientX, e.clientY)
  }

  const handlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId)
    }
    setIsDragging(false)
  }

  return ReactDOM.createPortal(
    <Overlay onClick={onCancel}>
      <Panel onClick={(e) => e.stopPropagation()}>
        <Title>{title}</Title>
        <Subtitle>{subtitle}</Subtitle>

        <PreviewFrame
          ref={frameRef}
          $dragging={isDragging}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
        >
          <PreviewImage src={imageUrl} alt={imageAlt} $scale={scale} $posX={posX} $posY={posY} draggable={false} />
          {scale > MIN_SCALE && <FocusMarker $posX={posX} $posY={posY} />}
        </PreviewFrame>

        <SliderRow>
          <ZoomIn size={16} color='#767676' />
          <SliderInput
            min={minScale}
            max={maxScale}
            value={scale}
            onChange={(e) => setScale(Number(e.target.value))}
            aria-label='Zoom da capa'
          />
          <ZoomValue>{Math.round(scale * 100)}%</ZoomValue>
        </SliderRow>

        <Actions>
          <CancelButton type='button' onClick={onCancel}>Cancelar</CancelButton>
          <SaveButton type='button' onClick={() => onConfirm(scale, posX, posY)}>Salvar</SaveButton>
        </Actions>
      </Panel>
    </Overlay>,
    document.body,
  )
}

export default CoverScaleModal
